import React, { useState } from 'react';
import { Button, IconButton, Menu, MenuItem, Box, Chip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useActiveVehicle } from 'containers/pages/CANpark/context/ActiveVehicleProvider';
import { TicketType } from 'containers/pages/CANpark/types/ticket';
import { useTicketTypeMeta } from 'containers/pages/CANpark/context/TicketTerminologyProvider';
import { ViolationDecision } from 'containers/pages/CANpark/utils/getNextViolationTicketType';

interface ViolationActionsProps {
	parkingAuthorization: {
		allowed: boolean;
		validFrom?: Date;
		validUntil?: Date;
	} | null;
	decision: ViolationDecision;
}

const ViolationActions: React.FC<ViolationActionsProps> = ({
	parkingAuthorization,
	decision,
}) => {
	const getTicketTypeMeta = useTicketTypeMeta();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const { goNext, ticketFormMethods, parkingLocation } = useActiveVehicle();
	const isHourly = parkingLocation?.violationCheckType === 'hourlyExpiry';

	const handleClick = (overrideViolation?: TicketType) => {
		const fallback =
			decision.action === 'suggestTicket' ? decision.ticketType : 'log';
		ticketFormMethods.setValue('ticketType', overrideViolation ?? fallback);
		goNext();
		handleMenuClose();
	};

	// Suppress primary CTA when no action is needed (hourly mode: within limit
	// or session already ticketed). The "More Actions" menu remains available
	// so operators can still force-create a ticket if needed.
	const suppressPrimaryCta =
		parkingAuthorization?.allowed ||
		decision.action === 'withinLimit' ||
		decision.action === 'sessionClosed';

	const primaryChipLabel = (() => {
		if (parkingAuthorization?.allowed) {
			return `Authorized until ${parkingAuthorization.validUntil?.toLocaleString()}`;
		}
		if (decision.action === 'withinLimit') {
			return `Within time limit — ${decision.minutesRemaining} min remaining`;
		}
		if (decision.action === 'sessionClosed') {
			return 'Already recorded this session';
		}
		return '';
	})();

	const primaryChipColor: 'success' | 'info' | 'default' = parkingAuthorization?.allowed
		? 'success'
		: decision.action === 'withinLimit'
		? 'success'
		: decision.action === 'sessionClosed'
		? 'info'
		: 'default';

	const primaryButtonMeta =
		decision.action === 'suggestTicket'
			? getTicketTypeMeta(decision.ticketType)
			: null;

	return (
		<Box
			display="flex"
			flexDirection="row"
			gap={2}
			width="100%"
			alignItems="center"
		>
			{suppressPrimaryCta ? (
				<Chip
					label={primaryChipLabel}
					color={primaryChipColor}
					variant="outlined"
					sx={{
						width: '100%',
					}}
				/>
			) : (
				<Button
					variant="outlined"
					color={primaryButtonMeta!.buttonColor}
					fullWidth
					onClick={() => handleClick()}
				>
					{isHourly && decision.action === 'suggestTicket' && decision.ticketType === 'log'
						? 'Record arrival'
						: primaryButtonMeta!.buttonText}
				</Button>
			)}

			{/* More Actions Button (Always Visible) */}
			<IconButton
				onClick={handleMenuOpen}
				aria-label="More actions"
				data-tour="ticket-action-more"
				sx={{
					border: 1,
					borderColor: 'divider',
				}}
			>
				<MoreVertIcon />
			</IconButton>

			{/* Dropdown Menu (Contains All Actions) */}
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleMenuClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				{(['log', 'logAndWarn', 'tow'] as const).map((type) => {
					const { buttonText } = getTicketTypeMeta(type);
					return (
						<MenuItem key={type} onClick={() => handleClick(type)}>
							{buttonText}
						</MenuItem>
					);
				})}
			</Menu>
		</Box>
	);
};

export default ViolationActions;
